import type { SubscriptionDetail } from '@hooks/subscription/models/subscription-models';

/** Days left at which a running trial starts to warn on every page. */
export const TRIAL_WARNING_DAYS = 3;

const DAY_MS = 86_400_000;

/**
 * Calendar days from today until the day `currentPeriodEnd` falls on, in the
 * viewer's local time zone; 0 means it ends today (or has already passed).
 * Calendar days rather than elapsed 24h blocks, so a trial that cuts off at
 * 23:00 tonight reads "today" all day instead of "1 day left" until 23:00.
 * null when the date is missing or unparseable.
 */
export function trialDaysLeftFrom(currentPeriodEnd: string | undefined, now: number = Date.now()): number | null {
  if (!currentPeriodEnd) return null;
  const end = new Date(currentPeriodEnd);
  if (Number.isNaN(end.getTime())) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(end) - startOfDay(new Date(now))) / DAY_MS);
  return days <= 0 ? 0 : days;
}

export interface TrialStatus {
  /** The account is on a free-trial plan that is running or has lapsed. */
  isTrial: boolean;
  /** The trial lapsed (UNPAID): nothing is owed, new submissions are blocked. */
  ended: boolean;
  /** Whole days until the trial cuts off; 0 = today; null when unknown or ended. */
  daysLeft: number | null;
  /** Running and within TRIAL_WARNING_DAYS of the cutoff. */
  endsSoon: boolean;
}

const NOT_TRIAL: TrialStatus = { isTrial: false, ended: false, daysLeft: null, endsSoon: false };

/**
 * One derivation of "where is this trial" shared by the subscription card,
 * the header badge and the shell banner, so the three can never disagree.
 * A trial that is PENDING or CANCELLED is not surfaced: the backend cancels
 * the trial itself once a paid plan is subscribed.
 */
export function deriveTrialStatus(
  detail: SubscriptionDetail | null | undefined,
  now: number = Date.now()
): TrialStatus {
  const sub = detail?.subscription;
  if (!sub || sub.plan?.isTrial !== true) return NOT_TRIAL;
  if (sub.status === 'UNPAID') return { isTrial: true, ended: true, daysLeft: null, endsSoon: false };
  if (sub.status !== 'ACTIVE') return NOT_TRIAL;
  const daysLeft = trialDaysLeftFrom(sub.currentPeriodEnd, now);
  return {
    isTrial: true,
    ended: false,
    daysLeft,
    endsSoon: daysLeft !== null && daysLeft <= TRIAL_WARNING_DAYS,
  };
}
